/**
 * Body-fixed surface points (S4-07, Concept §4.1 "landers sit on the right spot of the surface"): latitude, longitude
 * and altitude on a rotating body ↔ position (and velocity) relative to the body's centre in the inertial ecliptic
 * J2000 frame, in km and km/s.
 *
 * - Earth: geodetic latitude and height above the WGS 84 ellipsoid, with precession, nutation and sidereal time
 *   (astronomy-engine's observer functions) — launch pads are given that way.
 * - Every other body (the Moon first): planetocentric latitude and east longitude on a sphere of `radiusKm`, turned
 *   by the body's IAU rotation (`bodyOrientation`). The Moon's IAU model approximates the mean-Earth/polar-axis frame
 *   in which LRO gives landing sites.
 */
import { Observer, ObserverState, VectorObserver, Vector } from 'astronomy-engine';
import { type RotatingBodyId, bodyOrientation, eclipticToEqj, eqjToEcliptic } from './ephemeris';
import { tdbDaysToAstroTime } from './time';
import { AU_KM } from './units';
import { type Vec3, set, vec3 } from './vec3';

export interface SurfacePoint {
  /** Latitude in degrees (Earth: geodetic, other bodies: planetocentric). */
  lat: number;
  /** East longitude in degrees. */
  lon: number;
  /** Altitude in km (Earth: above the WGS 84 ellipsoid, other bodies: above the sphere). */
  alt: number;
  /**
   * Earth only: `lat` is the point's geocentric latitude, `alt` its height above the ellipsoid along the radius — the
   * convention of the Saturn V trajectory tables (NASA SP-4029). Other bodies are geocentric anyway.
   */
  geocentric?: boolean;
}

const DEG = Math.PI / 180;

/**
 * The Earth ellipsoid astronomy-engine's observer functions use (IERS: a = 6378.1366 km, b/a = 0.996647180302104;
 * within half a metre of WGS 84), so that the conversion below is exact for them.
 */
const EARTH_A = 6378.1366;
const EARTH_E2 = 1 - 0.996647180302104 ** 2;

/** Radius of the ellipsoid at a geocentric latitude, km. */
export function ellipsoidRadius(latDeg: number): number {
  const s = Math.sin(latDeg * DEG);
  return EARTH_A * Math.sqrt((1 - EARTH_E2) / (1 - EARTH_E2 * (1 - s * s)));
}

/** A point given by geocentric latitude and radius → geodetic latitude and height above the ellipsoid (km), exact. */
export function geocentricToGeodetic(latDeg: number, radiusKm: number): { lat: number; alt: number } {
  const z = radiusKm * Math.sin(latDeg * DEG);
  const p = radiusKm * Math.cos(latDeg * DEG);
  // fixed-point iteration on the latitude (< 1e-12 rad after a few steps for points near the surface)
  let phi = Math.atan2(z, p * (1 - EARTH_E2));
  let h = 0;
  for (let k = 0; k < 10; k++) {
    const n = EARTH_A / Math.sqrt(1 - EARTH_E2 * Math.sin(phi) ** 2);
    h = p / Math.cos(phi) - n;
    phi = Math.atan2(z, p * (1 - (EARTH_E2 * n) / (n + h)));
  }
  return { lat: phi / DEG, alt: h };
}
const KM_PER_S_PER_AU_PER_DAY = AU_KM / 86_400;
/** Half the time step for the surface velocity of bodies other than Earth (central difference), in days. */
const HALF_STEP_DAYS = 30 / 86_400;

const orientation = { north: vec3(), prime: vec3() };
const tmp = vec3();

/** Direction of (lat, lon) on a sphere with the orientation in `orientation`, times `r`. */
function sphericalPoint(out: Vec3, latDeg: number, lonDeg: number, r: number): Vec3 {
  const { north: n, prime: p } = orientation;
  // east = north × prime: 90° ahead of the prime meridian in the sense of rotation
  const ex = n.y * p.z - n.z * p.y;
  const ey = n.z * p.x - n.x * p.z;
  const ez = n.x * p.y - n.y * p.x;
  const cl = Math.cos(latDeg * DEG);
  const a = cl * Math.cos(lonDeg * DEG) * r;
  const b = cl * Math.sin(lonDeg * DEG) * r;
  const c = Math.sin(latDeg * DEG) * r;
  return set(out, a * p.x + b * ex + c * n.x, a * p.y + b * ey + c * n.y, a * p.z + b * ez + c * n.z);
}

/**
 * Position (and, if `outVel` is given, velocity) of the surface point `p` relative to the body's centre, inertial
 * ecliptic J2000, at `tdbDays`. `radiusKm` is the mean radius of the sphere (ignored for Earth, which uses an
 * ellipsoid).
 */
export function surfaceToInertial(
  body: RotatingBodyId,
  radiusKm: number,
  tdbDays: number,
  p: SurfacePoint,
  outPos: Vec3,
  outVel?: Vec3,
): Vec3 {
  if (body === 'earth') {
    const g = p.geocentric ? geocentricToGeodetic(p.lat, ellipsoidRadius(p.lat) + p.alt) : p;
    const s = ObserverState(tdbDaysToAstroTime(tdbDays), new Observer(g.lat, p.lon, g.alt * 1000), false);
    eqjToEcliptic(outPos, s.x, s.y, s.z, AU_KM);
    if (outVel) eqjToEcliptic(outVel, s.vx, s.vy, s.vz, KM_PER_S_PER_AU_PER_DAY);
    return outPos;
  }
  const r = radiusKm + p.alt;
  if (outVel) {
    bodyOrientation(body, tdbDays + HALF_STEP_DAYS, orientation);
    sphericalPoint(tmp, p.lat, p.lon, r);
    bodyOrientation(body, tdbDays - HALF_STEP_DAYS, orientation);
    sphericalPoint(outVel, p.lat, p.lon, r);
    const k = 1 / (2 * HALF_STEP_DAYS * 86_400);
    set(outVel, (tmp.x - outVel.x) * k, (tmp.y - outVel.y) * k, (tmp.z - outVel.z) * k);
  }
  bodyOrientation(body, tdbDays, orientation);
  return sphericalPoint(outPos, p.lat, p.lon, r);
}

/** The surface point below `pos` (relative to the body's centre, inertial ecliptic J2000) at `tdbDays`. */
export function inertialToSurface(
  body: RotatingBodyId,
  radiusKm: number,
  tdbDays: number,
  pos: Vec3,
  out: SurfacePoint,
): SurfacePoint {
  if (body === 'earth') {
    const t = tdbDaysToAstroTime(tdbDays);
    eclipticToEqj(tmp, pos.x, pos.y, pos.z, 1 / AU_KM);
    const o = VectorObserver(new Vector(tmp.x, tmp.y, tmp.z, t), false);
    out.lat = o.latitude;
    out.lon = o.longitude;
    out.alt = o.height / 1000;
    return out;
  }
  bodyOrientation(body, tdbDays, orientation);
  const { north: n, prime: p } = orientation;
  const ex = n.y * p.z - n.z * p.y;
  const ey = n.z * p.x - n.x * p.z;
  const ez = n.x * p.y - n.y * p.x;
  const a = pos.x * p.x + pos.y * p.y + pos.z * p.z;
  const b = pos.x * ex + pos.y * ey + pos.z * ez;
  const c = pos.x * n.x + pos.y * n.y + pos.z * n.z;
  const r = Math.hypot(a, b, c);
  out.lat = Math.asin(c / r) / DEG;
  out.lon = Math.atan2(b, a) / DEG;
  out.alt = r - radiusKm;
  return out;
}
